import {generateUUID} from './utils';
import {
    blockTextOffsetsToDocPositions,
    findBlockInDoc,
    findSnippetOffsetsInBlock,
    resolveStatementDocRange,
} from './blockRangeToDocPositions';
import {initializeReferenceMark} from '../yMaps/referencesYMap';
import useYDocStore from '../../../stores/useYDocStore';
import {setActiveMarginMarker} from '../plugins/marginMarkersPlugin';

function normalizeWhitespace(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
}

function findSnippetOffsetsInText(text, snippet) {
    if (!text || !snippet) return null;

    const direct = text.indexOf(snippet);
    if (direct !== -1) {
        return [direct, direct + snippet.length];
    }

    const normalizedSnippet = normalizeWhitespace(snippet);
    if (normalizedSnippet.length > 0) {
        const pattern = normalizedSnippet.split(/\s+/).map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('\\s+');
        const match = new RegExp(pattern, 'i').exec(text);
        if (match) {
            return [match.index, match.index + match[0].length];
        }
    }

    const words = normalizedSnippet.split(/\s+/).filter((w) => w.length > 2);
    if (words.length >= 3) {
        const prefix = words.slice(0, Math.min(6, words.length)).join('\\s+');
        const prefixMatch = new RegExp(prefix, 'i').exec(text);
        if (prefixMatch) {
            const start = prefixMatch.index;
            const endGuess = Math.min(text.length, start + normalizedSnippet.length + 20);
            return [start, endGuess];
        }
    }

    if (normalizeWhitespace(text).toLowerCase() === normalizedSnippet.toLowerCase()) {
        return [0, text.length];
    }

    return null;
}

/**
 * Search every block with a blockId for the snippet text.
 *
 * @returns {{blockId: string, start: number, end: number} | null}
 */
export function findStatementBlockAndOffsets(doc, preferredBlockId, text) {
    if (!doc || !text) return null;

    if (preferredBlockId) {
        const offsets = findSnippetOffsetsInBlock(doc, preferredBlockId, text);
        if (offsets) {
            return {blockId: preferredBlockId, start: offsets[0], end: offsets[1]};
        }

        const block = findBlockInDoc(doc, preferredBlockId);
        if (block) {
            const blockOffsets = findSnippetOffsetsInText(block.node.textContent, text);
            if (blockOffsets) {
                return {blockId: preferredBlockId, start: blockOffsets[0], end: blockOffsets[1]};
            }
        }
    }

    let found = null;
    doc.descendants((node, pos) => {
        if (found) return false;
        if (!node.isBlock || !node.attrs?.blockId) return undefined;

        const blockId = node.attrs.blockId;
        const offsets = findSnippetOffsetsInText(node.textContent, text);
        if (offsets) {
            found = {blockId, start: offsets[0], end: offsets[1]};
            return false;
        }
        return undefined;
    });

    return found;
}

function collectReferenceMarkRanges(doc, markType, uid) {
    const ranges = [];
    let current = null;

    doc.descendants((node, pos) => {
        if (!node.isText) return;

        const hasMark = node.marks.some((m) => m.type === markType && m.attrs.uid === uid);
        if (hasMark) {
            if (current && current.to === pos) {
                current.to = pos + node.nodeSize;
            } else {
                current = {from: pos, to: pos + node.nodeSize};
                ranges.push(current);
            }
        }
    });

    return ranges;
}

/**
 * If the statement text already sits inside a reference mark, return that uid.
 */
export function findExistingReferenceForStatement(view, text) {
    if (!view || !text) return null;

    const markType = view.state.schema.marks.reference;
    if (!markType) return null;

    const normSnippet = normalizeWhitespace(text).toLowerCase();
    if (!normSnippet) return null;

    const uids = new Set();
    view.state.doc.descendants((node) => {
        if (!node.isText) return;
        node.marks.forEach((m) => {
            if (m.type === markType && m.attrs.uid) {
                uids.add(m.attrs.uid);
            }
        });
    });

    for (const uid of uids) {
        const ranges = collectReferenceMarkRanges(view.state.doc, markType, uid);
        for (const range of ranges) {
            const markedText = normalizeWhitespace(
                view.state.doc.textBetween(range.from, range.to, ' ', ' ')
            ).toLowerCase();
            if (markedText.includes(normSnippet) || normSnippet.includes(markedText)) {
                return uid;
            }
        }
    }

    return null;
}

/**
 * Resolve doc positions for a citation-analysis statement, searching all blocks if needed.
 */
export function resolveStatementDocRangeExtended(view, blockId, range, text) {
    if (!view) return null;

    const direct = resolveStatementDocRange(view, blockId, range, text);
    if (direct) return direct;

    const located = findStatementBlockAndOffsets(view.state.doc, blockId, text);
    if (!located) return null;

    return blockTextOffsetsToDocPositions(
        view.state.doc,
        located.blockId,
        located.start,
        located.end
    );
}

/**
 * Apply (or focus) a reference mark for a citation-analysis statement.
 *
 * @returns {{ok: boolean, uid?: string, reason?: string, alreadyMarked?: boolean}}
 */
export function applyStatementReferenceMark(view, {blockId, range, text}) {
    if (!view) {
        return {ok: false, reason: 'Editor not available'};
    }

    const markType = view.state.schema.marks.reference;
    if (!markType) {
        return {ok: false, reason: 'Reference mark type missing from schema'};
    }

    const existingUid = findExistingReferenceForStatement(view, text);
    if (existingUid) {
        setActiveMarginMarker(view, {type: 'reference', uid: existingUid});
        view.focus();
        return {ok: true, uid: existingUid, alreadyMarked: true};
    }

    const docRange = resolveStatementDocRangeExtended(view, blockId, range, text);
    if (!docRange) {
        return {ok: false, reason: 'Could not locate statement text in the document'};
    }

    const uid = generateUUID();
    const mark = markType.create({uid});

    const {state, dispatch} = view;
    const tr = state.tr
        .addMark(docRange.from, docRange.to, mark)
        .removeStoredMark(markType);
    dispatch(tr);
    view.focus();

    const {ydoc, yReferenceMarks} = useYDocStore.getState();
    if (ydoc && yReferenceMarks) {
        ydoc.transact(() => {
            initializeReferenceMark(ydoc, yReferenceMarks, uid);
        }, null);
    } else {
        console.warn('[applyStatementReferenceMark] Y.Doc not ready — reference mark not written to yReferenceMarks');
    }

    setActiveMarginMarker(view, {type: 'reference', uid});
    return {ok: true, uid};
}
